import { Component, OnInit, ViewChild, Output, EventEmitter, ViewChildren } from '@angular/core';
import { style, state, animate, transition, trigger } from '@angular/animations';
import { Input } from "@angular/core";
import { WFWorker } from 'src/app/store/events.era';
import { newWorkerStages, createNewWorkerPermissions, NewWorkerStatus, workforceFeature, WorkforceActions } from 'src/app/store/workforce.era';
import { PermissionAccess } from 'src/app/store/permissions.era';
import { Contact } from 'src/app/store/contacts.era';
import { EVENT_PERMISSIONS } from 'src/app/services/event.permissions.service';
import { filterByKeywordAndProps } from 'src/app/helpers/helpers';
import { possibleContactsSelector } from 'src/app/store/reselectors/contacts.reselector';
import { AccessType } from 'globals/store/permissions.data';
import { Wrapper } from '../wrapper.component';
import { manageSessionPermissionReselector, manageSessionStates, ManageSessionStates } from '../../store/reselectors/sessions.reselector';
import { isEmailValid, jsonCopyObject } from 'globals/helpers/helpers';
import { MatFormFieldModule } from '@angular/material/form-field';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { IconComponent } from '../icon/icon.component';
import { MatRadioModule } from '@angular/material/radio';
import { EMAIL_REGEX } from 'globals/helpers/constants';

@Component({
    selector: 'app-manage-workers',
    templateUrl: './manage-workers.component.html',
    animations: [
        trigger('drop', [
            state('expand, nope', style({ height: '*' })),
            state('collapse, undefined, void', style({ height: '0', padding: 0 })),
            transition('* => expand', [animate('300ms')]),
            transition('void => *', [animate('0ms')]),
            transition('* => nope', [animate('0ms')])
        ])
    ],
    styleUrls: ['manage-workers.scss'],
    standalone: true,
    imports: [MatFormFieldModule, FormsModule,IconComponent, MatRadioModule, MatInputModule, CommonModule],
})
export class ManageWorkersComponent extends Wrapper<ManageWorkersComponent> implements OnInit {
    successMessage = '';
    errorMessage = '';
    checkEmailTimeout;
    EMAIL_REGEX = EMAIL_REGEX
    email_is_empty = true;
    contacts: Array<Contact> = [];
    possible_contacts: Array<Contact> = [];
    privileges_open = true;
    @ViewChild('emailEl') emailEl;
    @ViewChild('addForm') addForm;
    @ViewChildren('togglePrivilege') togglePrivilege;
    @Input('existing') existing
    @Input() no_animate
    @Input('emailinput') emailinput
    @Input() fromPeoplePicker = false
    @Output('completed') completed: EventEmitter<string> = new EventEmitter();
    @Output('progress') progress: EventEmitter<string> = new EventEmitter();
    _search_placeholder = 'Email address or search contacts'
    _email_placeholder = 'Email address'
    interchange_placeholder =  this._email_placeholder;
    _prev_email:string;
    newWorkerStages = newWorkerStages
    newWorkerStatus: NewWorkerStatus
    newWorker;
    public_account_info = {}
    permissions_model: AccessType
    account: any = null
    isValid = false // used for calling components to IMMEDIATELY TEST VALUES
    isInviting = false
    workersByEmail: { [key:string]: WFWorker } = {}
    selectedContacts: Set<String> = new Set()
    selectable_permission_levels = []
    
    readState: keyof ManageSessionStates = manageSessionStates.ReadOnly
    guessedEmail = false

    constructor() {
        super()
        this.selectCallback(manageSessionPermissionReselector, null, (readState: keyof ManageSessionStates) => {
          this.readState = readState
          if (readState && !this.permissions_model) this.permissions_model = readState === 'Logistics' ? 'logistics' : 'program'
        })
        this.subProp(this.store.select('workforce', 'newWorker'), 'newWorker')
        this.subProp(this.store.select('workforce', 'workersByEmail'), 'workersByEmail')
        this.subCallback(this.store.select('workforce', 'newWorkerStatus'), newWorkerStatus => {
          this.newWorkerStatus = newWorkerStatus
          this.isInviting = [newWorkerStages.send_invite as string, newWorkerStages.invite_existing as string].includes(newWorkerStatus)
        })

        this.subCallback(this.store.select('accounts'), ({account, public_info}) => {
          this.account = account
          this.public_account_info = public_info;
        })

        this.selectCallback(possibleContactsSelector, null, (possibleContacts: Array<Contact>) => {
          this.possible_contacts = this.contacts = possibleContacts
          if(this.possible_contacts.length > 0) this.interchange_placeholder = this._search_placeholder;
        })
        
        if(!this.isInviting) this.store.dispatch(WorkforceActions.getFreshWorker());
        this.dispatchPermissions() // default;
        this.workforceService.invite_as_account_role = null;
        this.workforceService.extend_invite = false;

        this.selectable_permission_levels = Object.keys(EVENT_PERMISSIONS).map(key=>EVENT_PERMISSIONS[key]).filter(level=>level.selectable_level)
        // console.log('this.selectable_permission_levels ', this.selectable_permission_levels);
    }

    ngOnInit() {
      // console.log("Inputs: ", this.existing, this.emailinput)
      if (this.emailinput) { // if adding from people picker, you can initialise this with an existing input value
        this.store.dispatch(WorkforceActions.updateNewWorker({email:this.emailinput}))
        this.focusOnSwappedSearchValue()
      }
    }

    focusOnSwappedSearchValue(){
        setTimeout(_ => {
            if (this.emailEl) this.emailEl.nativeElement.focus();
            this.isEmailEmpty({ target: { value: this.emailinput } });
        })
    }

    dispatchPermissions(){
      const permissions:PermissionAccess = createNewWorkerPermissions()
      permissions.types.push(this.permissions_model)
      this.store.dispatch(WorkforceActions.updateNewWorkerPermissions(permissions))
    }

    onClickDispatchStatus(status: NewWorkerStatus){
      // console.log("On click dispatch status called", { status })
      this.store.dispatch(WorkforceActions.changeNewWorkerStatus(status))
    }


    changeInput($event, key){
      const value = $event.target.value
      console.log($event, { key, value })
      if(key === 'email'){
        this.guessNameFromEmail(value)
        const emailRegex = new RegExp(EMAIL_REGEX)
        if(!emailRegex.test(value)) return;
      }
      this.store.dispatch(WorkforceActions.updateNewWorker({[key]:value}))
      this.updateFormValidation()
    }

    toggleContactChecked({ email }: Contact) {
      if (this.selectedContacts.has(email)) {
        this.selectedContacts.delete(email)
      } else if (this.fromPeoplePicker) {
        this.selectedContacts = new Set([ email ])
      } else {
        this.selectedContacts.add(email)
      }
    }

    getSelectedWorkers(): WFWorker[] {
      if (this.possible_contacts.length === 1) this.selectedContacts = new Set([this.possible_contacts[0].email]) // if possible_contacts has only one contact, and this method was called, it means that the email address and a single possible contact is showing.  Since that email address could be shorter than the actual contact's email, we select it as we continue onto the is_inviting phase
      const selectedWorkers:WFWorker[] = jsonCopyObject(this.possible_contacts)
        .filter(({ email }) => this.selectedContacts.has(email))
        .map(contact=>{
          if (contact.hasOwnProperty('role')) delete contact.role;
          if (contact.hasOwnProperty('accountId')) delete contact.accountId
          return contact;
        })
      // console.log({ possible_contacts: jsonCopyObject(this.possible_contacts), selectedContacts: this.selectedContacts, selectedWorkers })
      return selectedWorkers
    }

    updateFormValidation() {
      this.workforceService.invite_valid = !this.workforceService.extend_invite || !!this.workforceService.invite_as_account_role;
    }

    validityCheck(firstName, lastName, email) {
      const emailIsValid = isEmailValid(email)
      if (emailIsValid && !firstName && !lastName && !this.guessedEmail) {
        this.guessedEmail = true
        const update_obj = this.guessNameFromEmail(email)
        if(update_obj['firstName']) firstName = update_obj['firstName']
        if(update_obj['lastName']) lastName = update_obj['lastName']
      }
      if (emailIsValid && this.newWorkerStatus !== newWorkerStages.email_address) {
        // having the buttons pop up only after changing to a non-workforce member is strange, so moving that here
        this.store.dispatch(WorkforceActions.changeNewWorkerStatus('email_address'))
      }
      if(emailIsValid && this._prev_email !== email){
        this.store.dispatch(WorkforceActions.updateNewWorker({email}))
        this._prev_email = email;
      }
      this.isValid = firstName && lastName && !this.workersByEmail[email] && emailIsValid
      // console.log({ firstName, lastName, email, emailIsValid, isValid: this.isValid })
    }

    checkName(firstName, lastName) {
      this.validityCheck(firstName, lastName, this.newWorker.email)
    }

    isEmailEmptyWithAPasteDelay($event){
      console.log({ value: $event.target.value })
      setTimeout(()=>{
        this.isEmailEmpty($event, $event.target.value)
      })
    }

    isEmailEmpty($event, email?) {

      if (email) {
        this.validityCheck(this.newWorker.firstName, this.newWorker.lastName, email)
      }
      // console.log('isEmailEmpty ',$event);
      this.email_is_empty = false;
      const searchTerm = $event.target.value
      if (!searchTerm) {
          this.email_is_empty = true;
          this.completed.emit('reset')
      }
      const filterFunction = filterByKeywordAndProps(searchTerm, ['firstName', 'lastName', 'email'])
      this.possible_contacts = !$event.target.value ? this.contacts : this.contacts.filter(filterFunction)
      // console.log({ contacts: this.contacts, possible_contacts: this.possible_contacts })
      this.interchange_placeholder = (this.possible_contacts.length > 0) ? this._search_placeholder : this._email_placeholder;
      
      if(this.possible_contacts.length === 0 || !searchTerm){
        this.selectedContacts.clear();
      }
      clearTimeout(this.checkEmailTimeout);
      if (!this.workersByEmail[email]) {
        this.workforceService.errorMessage = null
      }

      this.checkEmailTimeout = setTimeout(_ => { // this gives the user time to complete the email before checking
        // console.log('this.email ', this.email);
        if (this.workersByEmail[email]) {
          this.workforceService.errorMessage = `A user already exists in the workforce with that email`
        }
      }, 500);
    }
    guessNameFromEmail(email) {
      const full_name = email.split('@')[0];
      if (!full_name || (this.newWorker.firstName && this.newWorker.lastName)) return;
      const name_bits = full_name.split(/[._-\d+]{1,}/g);
      for (let i = name_bits.length - 1; i > 0; i--) {
          if (name_bits[i] === "") { name_bits.pop(); }
      }
      const update_obj = {email}
      if (!this.newWorker.firstName) update_obj['firstName'] = this.uiService.jsUcfirst(name_bits[0]);
      if (!this.newWorker.lastName) update_obj['lastName'] = (name_bits.length > 1) ? this.uiService.jsUcfirst(name_bits[name_bits.length - 1]) : '';
      if(update_obj['firstName'] || update_obj['lastName']) this.store.dispatch(WorkforceActions.updateNewWorker(update_obj))
      return update_obj;
    }
}
